/**
 * WorkStreamTemplates — starter-template gallery (Story 4).
 *
 * A static registry of 3 starter templates grounded in the aprio-dental
 * synthesis research (Carrie's real sidebar history + Cluster 7), so the happy
 * path through the create wizard is obvious. Each template is a
 * `{ id, name, description, suggestedTrigger }` tuple rendered as a selectable
 * chip. Selecting one pre-fills the wizard's name + trigger.
 *
 * Static data — no `GET` call renders this gallery.
 */

import { Workflow, Calendar, Zap, MessageSquare } from 'lucide-react';
import type { WorkStreamTriggerType } from './types';

export interface WorkStreamTemplate {
  id: string;
  name: string;
  description: string;
  suggestedTrigger: WorkStreamTriggerType;
  /** which synthesis cluster/history the template is grounded in */
  source: string;
}

/**
 * Templates from synthesis.md — real, not invented:
 *  1. "QBO Book Close Status" — Cluster 7 (On-Demand)
 *  2. "State Tax Payment Reconciliation" — Carrie's sidebar history (Schedule)
 *  3. "Fathom Comparison" — Carrie's sidebar history (Schedule)
 */
export const WORKSTREAM_TEMPLATES: readonly WorkStreamTemplate[] = [
  {
    id: 'qbo-book-close-status',
    name: 'QBO Book Close Status',
    description: 'Check when the QuickBooks book was closed.',
    suggestedTrigger: 'on-demand',
    source: 'Cluster 7',
  },
  {
    id: 'state-tax-payment-reconciliation',
    name: 'State Tax Payment Reconciliation',
    description: 'Reconcile state tax payments against the expense account.',
    suggestedTrigger: 'schedule',
    source: "Carrie's sidebar history",
  },
  {
    id: 'fathom-comparison',
    name: 'Fathom Comparison',
    description: 'Compare Fathom reporting figures period-over-period.',
    suggestedTrigger: 'schedule',
    source: "Carrie's sidebar history",
  },
] as const;

export function getTemplateById(id: string | null): WorkStreamTemplate | undefined {
  if (!id) return undefined;
  return WORKSTREAM_TEMPLATES.find(t => t.id === id);
}

const TRIGGER_META: Record<
  WorkStreamTriggerType,
  { label: string; icon: typeof Calendar }
> = {
  schedule: { label: 'Schedule', icon: Calendar },
  'on-demand': { label: 'On-Demand', icon: Zap },
  'from-chat': { label: 'From-Chat', icon: MessageSquare },
};

interface WorkStreamTemplatesProps {
  /** currently selected template id, or null */
  selectedId: string | null;
  /** called when a template chip is selected (or deselected) */
  onSelect: (id: string) => void;
  className?: string;
}

/**
 * Renders the 3 starter templates as selectable chips. Mirrors the visual
 * language of the WS-FE-008 "Create your first Blueprint" CTA — same card/chip
 * tokens, no hardcoded colors.
 */
export function WorkStreamTemplates({ selectedId, onSelect, className }: WorkStreamTemplatesProps) {
  return (
    <div className={className} role="group" aria-label="Starter templates">
      {WORKSTREAM_TEMPLATES.map(template => {
        const selected = template.id === selectedId;
        const TriggerIcon = TRIGGER_META[template.suggestedTrigger].icon;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template.id)}
            aria-pressed={selected}
            className={`
              w-full text-left p-3 rounded-lg border transition-all duration-200
              focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400
              ${selected
                ? 'border-primary-500 bg-primary-50 shadow-warm-sm'
                : 'border-line bg-surface-card hover:border-primary-300 hover:bg-surface-subtle'}
            `}
          >
            <div className="flex items-start gap-2.5">
              <div className={`
                w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                ${selected ? 'bg-primary-100 text-primary-700' : 'bg-surface-subtle text-muted'}
              `}>
                <Workflow size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-text truncate">{template.name}</h4>
                  <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-medium text-subtle uppercase tracking-wide">
                    <TriggerIcon size={10} />
                    {TRIGGER_META[template.suggestedTrigger].label}
                  </span>
                </div>
                <p className="text-xs text-muted leading-relaxed mt-0.5">{template.description}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
